import { getCookie } from "@/lib/auth/cookies";
import {
  changeEventCompany,
  createEvent as createEventApi,
  getEvents as getEventsApi,
  deleteEvent as deleteEventApi,
  updateEvent as updateEventApi,
  updateEventSidebarOrder as updateEventSidebarOrderApi,
  updateEventGuestDetailLayout as updateEventGuestDetailLayoutApi,
  updateEventGuestDetailLayouts as updateEventGuestDetailLayoutsApi,
  updateEventDetailLayouts as updateEventDetailLayoutsApi,
  updateEventPlugins as updateEventPluginsApi,
  updateEventAppConfig as updateEventAppConfigApi,
  updateEventRegistrationConfig as updateEventRegistrationConfigApi,
  getEvent as getEventApi,
  getEventGuestQuota as getEventGuestQuotaApi,
  getValidEventCount,
  archiveEvent,
  archiveEventsBulk,
  deleteEventsBulk,
  BulkActionResult,
  createAdditionalField as createAdditionalFieldApi,
  deleteAdditionalField as deleteAdditionalFieldApi,
  changeBlockedCountForEvent as changeBlockedCountForEventApi,
  duplicateEvent as duplicateEventApi,
} from "@/lib/api/clients/EventClient";
import type {
  CreateEventResponse,
  DuplicateEventResponse,
} from "@/lib/api/clients/EventClient";
import { BulkSelection } from "@/types/BulkSelection";
import {
  handleErrorServer,
  throwServerError,
  ServerError,
} from "@/lib/api/errors/ErrorHandler";
import {
  AppConfig,
  Event,
  EventDetailLayouts,
  GuestDetailLayout,
  NamedGuestDetailLayout,
} from "../../../types/Event";
import { Filter } from "@/types/Filter";
import { EventGuestQuota } from "@/types/EventGuestQuota";
import { EventRowDTO } from "@/types/EventRowDTO";
import { EventRoleCreationDTO } from "@/types/EventRoleCreationDTO";
import { Field } from "@/types/Field";
import { Guest } from "@/types/Guest";
import { isValidEntityName, isValidObjectId } from "@/lib/utils/input-validator";
import { INVALID_STRING_PATTERN } from "@/lib/utils/default-returner";
import { createGuests } from "./GuestHandling";
import { createField } from "./AdditionalFieldHandling";
import { AdditionalFieldEvent } from "@/types/AdditionalFieldEvent";

// Audit item 6 — mirror server caps in EventValidation.java so a too-long
// zipCode (server max 20, client previously allowed 70) doesn't pass the
// client gate only to 400 at the server. Fields without an explicit @Size
// on the backend get the server's actual @Size value here.
export const EVENT_FIELD_CAPS = {
  name: 255,
} as const;
export const EVENT_OPTIONAL_FIELD_CAPS = {
  locationName: 200,
  address: 200,
  city: 200,
  zipCode: 20,
  country: 200,
} as const;

export async function changeBlockedCountForEvent(
  eventId: string,
  blockedCount: number,
): Promise<void | ServerError> {
  try {
    const companyId = await getCookie("companyId");

    if (!isValidObjectId(eventId)) throw new Error("validationError");
    if (!isValidObjectId(companyId ?? "")) throw new Error("validationError");
    if (blockedCount === null || blockedCount === undefined)
      throw new Error("validationError");

    await changeBlockedCountForEventApi(
      eventId,
      companyId!,
      blockedCount,
    );
  } catch (error) {
    return handleErrorServer(error);
  }
}

export async function createAdditionalField(
  eventId: string,
  fieldKey: string,
  fieldValue: AdditionalFieldEvent,
): Promise<void | ServerError> {
  try {

    if (!isValidObjectId(eventId)) throw new Error("validationError");
    if (
      !fieldKey ||
      fieldKey.trim().length < 1 ||
      fieldKey.length > 100 ||
      INVALID_STRING_PATTERN.test(fieldKey)
    )
      throw new Error("validationError");
    if (!fieldValue || !fieldValue.type) throw new Error("validationError");
    if (fieldValue.value !== null && fieldValue.value !== undefined) {
      if (Array.isArray(fieldValue.value)) {
        if (fieldValue.value.length === 0) throw new Error("validationError");
        if (fieldValue.value.some((v) => typeof v !== "string"))
          throw new Error("validationError");
      } else if (typeof fieldValue.value !== "string") {
        throw new Error("validationError");
      }
    }

    await createAdditionalFieldApi(eventId, fieldKey, fieldValue);
  } catch (error) {
    return handleErrorServer(error);
  }
}

export async function deleteAdditionalField(
  eventId: string,
  fieldKey: string,
): Promise<void | ServerError> {
  try {

    if (!isValidObjectId(eventId)) throw new Error("validationError");
    if (
      !fieldKey ||
      fieldKey.trim().length < 1 ||
      fieldKey.length > 100 ||
      INVALID_STRING_PATTERN.test(fieldKey)
    )
      throw new Error("validationError");

    await deleteAdditionalFieldApi(eventId, fieldKey);
  } catch (error) {
    return handleErrorServer(error);
  }
}

export async function changeEventHostCompany(
  eventId: string,
): Promise<void | ServerError> {
  try {
    const companyId = await getCookie("companyId");

    return await changeEventCompany(companyId!, eventId);
  } catch (error) {
    return handleErrorServer(error);
  }
}

export async function getEventCount(
  archived: boolean,
  filters: Filter[],
  searchTerm: string = "",
): Promise<number | ServerError> {
  try {
    return await getValidEventCount(archived, filters, searchTerm);
  } catch (error) {
    return handleErrorServer(error);
  }
}
async function doBulkEventArchive(
  selection: BulkSelection,
  archive: boolean,
): Promise<BulkActionResult | ServerError> {
  try {
    if (selection.mode === "ids") {
      if (selection.ids.length === 0) throw new Error("validationError");
      const invalidIds = selection.ids.filter((id) => !isValidObjectId(id));
      if (invalidIds.length > 0) throw new Error("validationError");
      return await archiveEventsBulk(
        { eventIds: selection.ids, archive },
      );
    }
    return await archiveEventsBulk(
      {
        filters: selection.filters,
        excludeIds: selection.excludeIds,
        search: selection.search,
        archived: selection.archived,
        archive,
      },
    );
  } catch (error) {
    return handleErrorServer(error);
  }
}

export async function archiveEvents(
  selection: BulkSelection,
): Promise<BulkActionResult | ServerError> {
  return doBulkEventArchive(selection, true);
}

export async function unarchiveEvents(
  selection: BulkSelection,
): Promise<BulkActionResult | ServerError> {
  return doBulkEventArchive(selection, false);
}

export async function deleteEvents(
  selection: BulkSelection,
): Promise<BulkActionResult | ServerError> {
  try {
    if (selection.mode === "ids") {
      if (selection.ids.length === 0) throw new Error("validationError");
      const invalidIds = selection.ids.filter((id) => !isValidObjectId(id));
      if (invalidIds.length > 0) throw new Error("validationError");
      return await deleteEventsBulk({ eventIds: selection.ids });
    }
    return await deleteEventsBulk(
      {
        filters: selection.filters,
        excludeIds: selection.excludeIds,
        search: selection.search,
        archived: selection.archived,
      },
    );
  } catch (error) {
    return handleErrorServer(error);
  }
}

export async function getEvent(
  eventId: string,
  field: string,
): Promise<Event | ServerError> {
  try {

    if (!isValidObjectId(eventId)) throw new Error("validationError");

    return await getEventApi(eventId, field);
  } catch (error) {
    return handleErrorServer(error);
  }
}

export async function getEventGuestQuota(
  eventId: string,
): Promise<EventGuestQuota | ServerError> {
  try {

    if (!isValidObjectId(eventId)) throw new Error("validationError");

    return await getEventGuestQuotaApi(eventId);
  } catch (error) {
    return handleErrorServer(error);
  }
}

export async function getEvents(
  fields: string,
  searchTerm: string,
  page: number,
  size: number,
  filter: Filter[],
  sortBy: string = "createdAt",
  sortOrder: "asc" | "desc" = "desc",
): Promise<EventRowDTO[] | ServerError> {
  try {

    if (size < 1 || size > 100) throw new Error("validationError");
    if (page < 0) throw new Error("validationError");

    const response = await getEventsApi(
      fields + ",eventId,plugins,registrationType,additionalFields",
      searchTerm,
      page,
      size,
      filter,
      sortBy,
      sortOrder,
    );

    if (!Array.isArray(response)) throw new Error("unknown");

    return response;
  } catch (error) {
    return handleErrorServer(error);
  }
}

export async function updateEvents(
  events: EventRowDTO[],
): Promise<void | ServerError> {
  try {
    for (const event of events) {
      if (event.start && event.end && new Date(event.start) > new Date(event.end)) {
        throw new Error("validationError");
      }
    }


    await Promise.all(
      events.map((event) => updateEventApi(event.eventId!, event)),
    );
  } catch (error) {
    return handleErrorServer(error);
  }
}

export async function updateEvent(
  eventId: string,
  event: EventRowDTO,
  ifMatch?: string,
): Promise<void | ServerError> {
  try {
    if (event.start && event.end && new Date(event.start) > new Date(event.end)) {
      throw new Error("validationError");
    }

    if (event.name !== undefined && !isValidEntityName(event.name, EVENT_FIELD_CAPS.name)) {
      throw new Error("validationError");
    }

    for (const [key, max] of Object.entries(EVENT_OPTIONAL_FIELD_CAPS) as Array<
      [keyof EventRowDTO, number]
    >) {
      const value = event[key];
      if (typeof value === "string" && value.length > 0 && !isValidEntityName(value, max)) {
        throw new Error("validationError");
      }
    }


    await updateEventApi(eventId, event, ifMatch);
  } catch (error) {
    return handleErrorServer(error);
  }
}

// F22 narrow server actions — EVENT-EDITOR-allowed backend endpoints.
// Use these instead of updateEvent() when the field being mutated is in
// the EDITOR-scoped set (sidebar order, plugin toggle, registration
// mode). updateEvent() itself now requires ADMIN+ on the backend.

export async function updateEventSidebarOrder(
  eventId: string,
  sidebarOrder: string[],
): Promise<void | ServerError> {
  try {
    await updateEventSidebarOrderApi(eventId, sidebarOrder);
  } catch (error) {
    return handleErrorServer(error);
  }
}

export async function updateEventGuestDetailLayout(
  eventId: string,
  guestDetailLayout: GuestDetailLayout,
): Promise<void | ServerError> {
  try {
    if (!isValidObjectId(eventId)) throw new Error("validationError");
    await updateEventGuestDetailLayoutApi(eventId, guestDetailLayout);
  } catch (error) {
    return handleErrorServer(error);
  }
}

/**
 * Named-layouts whole-array save. A stale `ifMatch` returns a ServerError
 * with message `staleIfMatch` — the builder shows a conflict banner instead
 * of silently overwriting a concurrent edit.
 */
export async function updateEventGuestDetailLayouts(
  eventId: string,
  guestDetailLayouts: NamedGuestDetailLayout[],
  ifMatch?: string,
): Promise<void | ServerError> {
  try {
    if (!isValidObjectId(eventId)) throw new Error("validationError");
    await updateEventGuestDetailLayoutsApi(eventId, guestDetailLayouts, ifMatch);
  } catch (error) {
    return handleErrorServer(error);
  }
}

/**
 * Per-entity detail-popup config, MERGED per entity key — pass only the
 * entities you edited (`{ speaker: {...} }`), never the whole map, so a
 * parallel sponsor edit survives. Within an entity always send the COMPLETE
 * object (Standard + `layouts`).
 *
 * A stale `ifMatch` returns a ServerError with message `staleIfMatch` — the
 * builder shows a conflict banner instead of overwriting a concurrent edit.
 */
export async function updateEventDetailLayouts(
  eventId: string,
  detailLayouts: EventDetailLayouts,
  ifMatch?: string,
): Promise<void | ServerError> {
  try {
    if (!isValidObjectId(eventId)) throw new Error("validationError");
    await updateEventDetailLayoutsApi(eventId, detailLayouts, ifMatch);
  } catch (error) {
    return handleErrorServer(error);
  }
}

export async function updateEventPlugins(
  eventId: string,
  plugins: string[],
): Promise<void | ServerError> {
  try {
    await updateEventPluginsApi(eventId, plugins);
  } catch (error) {
    return handleErrorServer(error);
  }
}

export async function updateEventAppConfig(
  eventId: string,
  appConfig: AppConfig,
  ifMatch?: string,
  /** Art. 50 KI-VO: the AI lane, forwarded as the body's write-only `aiContentTool`. */
  aiContentTool?: string,
): Promise<void | ServerError> {
  try {
    if (!isValidObjectId(eventId)) throw new Error("validationError");
    await updateEventAppConfigApi(eventId, appConfig, ifMatch, aiContentTool);
  } catch (error) {
    return handleErrorServer(error);
  }
}

export async function updateEventRegistrationConfig(
  eventId: string,
  config: {
    defaultRegistrationMode?: string;
    defaultRegistrationConditions?: Record<string, unknown>;
    defaultRegistrationMessage?: string;
  },
): Promise<void | ServerError> {
  try {
    await updateEventRegistrationConfigApi(eventId, config);
  } catch (error) {
    return handleErrorServer(error);
  }
}

export async function createEvent(
  event: Event,
  collaborators: EventRoleCreationDTO[] = [],
  image?: { buffer: ArrayBuffer; fileName: string; mimeType: string },
  /** Art. 50 KI-VO: the wizard's AI lane, forwarded as `?aiContentTool=`. */
  aiContentTool?: string,
): Promise<CreateEventResponse | ServerError> {
  try {
    const companyId = await getCookie("companyId");

    if (!isValidObjectId(companyId!)) throw new Error("validationError");
    if (!event.start || !event.end) throw new Error("validationError");
    if (new Date(event.start) > new Date(event.end))
      throw new Error("validationError");
    if (!isValidEntityName(event.name, EVENT_FIELD_CAPS.name)) throw new Error("validationError");

    for (const [key, max] of Object.entries(EVENT_OPTIONAL_FIELD_CAPS) as Array<
      [keyof Event, number]
    >) {
      const value = event[key];
      if (typeof value === "string" && value.length > 0 && !isValidEntityName(value, max)) {
        throw new Error("validationError");
      }
    }
    // Free-text fields: length-only (they may contain emoji / punctuation the
    // name validator forbids); the backend enforces the same caps + URL shape.
    if (event.description && event.description.length > 5000) throw new Error("validationError");
    if (event.onlineUrl && event.onlineUrl.length > 2000) throw new Error("validationError");
    return await createEventApi(event, companyId!, collaborators, image, aiContentTool);
  } catch (error) {
    return handleErrorServer(error);
  }
}

export async function duplicateEvent(
  eventId: string,
  name?: string,
  copyGuests?: boolean,
): Promise<DuplicateEventResponse | ServerError> {
  try {

    if (!isValidObjectId(eventId)) throw new Error("validationError");
    const normalizedName = name?.trim();
    if (
      normalizedName !== undefined &&
      (
        normalizedName.length < 1 ||
        normalizedName.length > 70 ||
        INVALID_STRING_PATTERN.test(normalizedName)
      )
    ) {
      throw new Error("validationError");
    }

    return await duplicateEventApi(eventId, normalizedName, copyGuests);
  } catch (error) {
    return handleErrorServer(error);
  }
}

export async function getSearchEvents(searchTerm: string = ""): Promise<
  | {
      eventId: string;
      name: string;
      plugins: string[];
      role: string;
      archived: boolean;
      capabilities: string[];
    }[]
  | ServerError
> {
  try {
    const allEvents: EventRowDTO[] = [];
    let page = 0;

    // Paginate through all results (100 per page). `capabilities` resolves the
    // CUSTOM-role allowlist so ⌘K can gate each event's pages to what the role
    // may actually open.
    while (true) {
      const batch = await getEventsApi(
        "name,eventId,plugins,role,archived,capabilities",
        searchTerm,
        page,
        100,
        [],
        "createdAt",
        "desc",
      );

      if (!Array.isArray(batch)) throw new Error("unknown");

      allEvents.push(...batch);

      // If we got fewer than 100, we've reached the last page
      if (batch.length < 100) break;
      page++;
    }

    return allEvents.map((e) => ({
      eventId: e.eventId!,
      name: e.name!,
      plugins: (e.plugins as string[]) ?? [],
      role: e.role ?? "",
      archived: e.archived ?? false,
      capabilities: (e.capabilities as string[]) ?? [],
    }));
  } catch (error) {
    return handleErrorServer(error);
  }
}

export async function createInitialEvent(
  event: Event,
  field: Field | null,
  guests: Guest[] | null,
  collaborators: EventRoleCreationDTO[] = [],
  image?: { buffer: ArrayBuffer; fileName: string; mimeType: string },
  /**
   * Art. 50 Abs. 2 KI-VO: the create wizard's AI lane
   * (`apply_event_wizard_state`) — the server stamps the new event's
   * guest-content mark. It covers the guest-role field created right after,
   * which is part of the same event.
   */
  aiContentTool?: string,
): Promise<CreateEventResponse | ServerError> {
  try {
    const created = await createEvent(event, collaborators, image, aiContentTool);

    if (
      created &&
      typeof created === "object" &&
      "isServerError" in created &&
      created.isServerError
    ) {
      return created;
    }

    if (!created || typeof created !== "object" || !("eventId" in created)) {
      throw new Error("validationError");
    }

    event.eventId = created.eventId;

    const promiseField = field
      ? createField(event.eventId!, field)
      : Promise.resolve();
    const promiseGuests = guests
      ? createGuests(event.eventId!, guests)
      : Promise.resolve();

    const response = await Promise.all([promiseField, promiseGuests]);

    const [fieldResponse, guestsResponse] = response;

    if (
      fieldResponse &&
      typeof fieldResponse === "object" &&
      "message" in fieldResponse
    ) {
      throwServerError(fieldResponse as ServerError);
    }
    if (guestsResponse && "message" in guestsResponse) {
      throwServerError(guestsResponse as ServerError);
    }

    return created;
  } catch (error) {
    return handleErrorServer(error);
  }
}
